import background from '@/assets/images/workspaces-bg.png';
import { Heading } from '@/components/WorksSpaces/Heading.tsx';
import { NoAccounts } from '@/components/WorksSpaces/NoAccounts.tsx';
import { WorkspacesList } from '@/components/WorksSpaces/WorkspacesList.tsx';
import { Bars3Icon, BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Account } from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@superfunnel/ui-core/v2/Collapsible';
import { QueryDocumentSnapshot, updateDoc } from 'firebase/firestore';
import { Plus } from 'iconoir-react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { AuthContext } from '../contexts/AuthContext';
import { UserContext } from '../contexts/UserContext';
import { useWhitelabel } from '../contexts/WhitelabelContext';
import CreateWorkspaceDialog from '../modals/CreateWorkspaceModal';

export default function Workspaces() {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { accounts, refetchAccounts } = useContext(UserContext);

  const [createWorkspaceDialogOpen, setCreateWorkspaceDialogOpen] =
    useState(false);
  const [showDeleteWorkspaceModal, setShowDeleteWorkspaceModal] =
    useState<QueryDocumentSnapshot<Account>>();

  // Redirect to onboard if user has no accounts
  useEffect(() => {
    if (accounts !== undefined && accounts.length === 0) {
      track('workspaces_no_accounts_redirect_to_onboard');
      navigate('/onboard');
    }
  }, [accounts, navigate, track]);

  // Track workspaces screen view
  useState(() => {
    track('workspaces_screen_viewed', {
      workspaces_count: accounts?.length || 0,
      has_workspaces: (accounts?.length || 0) > 0,
    });
  });

  return (
    <div
      className="bg-position-[center_top] h-screen overflow-scroll bg-cover bg-no-repeat"
      style={{ backgroundImage: `url(${background})` }}
    >
      <CreateWorkspaceDialog
        open={createWorkspaceDialogOpen}
        onClose={() => setCreateWorkspaceDialogOpen(false)}
      />
      <AlertModal
        type="warn"
        title={`Are you sure you'd like to delete account ${
          showDeleteWorkspaceModal?.data().name
        }?`}
        description="This is not reversible"
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: () => {
              track('delete_workspace_cancelled', {
                account_name: showDeleteWorkspaceModal?.data().name,
              });
              setShowDeleteWorkspaceModal(undefined);
            },
          },

          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            onClick: async () => {
              const account = showDeleteWorkspaceModal;
              if (!account) return;

              track('workspace_deleted', {
                account_name: account.data().name,
                workspaces_count: accounts?.length || 0,
              });
              setShowDeleteWorkspaceModal(undefined);

              updateDoc(account.ref, { status: 'deleted' })
                .then(() => {
                  track('workspace_delete_success', {
                    account_name: account.data().name,
                  });
                  refetchAccounts();
                })
                .catch((error) => {
                  track('workspace_delete_failed', {
                    account_name: account.data().name,
                    error: error.message,
                  });
                  throw error;
                });
            },
          },
        ]}
        open={!!showDeleteWorkspaceModal}
        onClose={() => {
          setShowDeleteWorkspaceModal(undefined);
        }}
      />
      <div className="flex h-full flex-col">
        <Header
          onNewWorkspaceClick={() => {
            track('create_workspace_button_clicked', {
              workspaces_count: accounts?.length || 0,
            });
            setCreateWorkspaceDialogOpen(true);
          }}
        />
        <div className="mx-auto flex w-full max-w-[1440px] flex-1 flex-col px-4 pb-6 lg:px-8">
          <Heading />
          {!!accounts?.length && (
            <div className="mx-auto w-full max-w-[1200px]">
              <WorkspacesList
                accounts={accounts}
                onDeleteAccountClick={(account) => {
                  track('delete_workspace_clicked', {
                    account_name: account.data().name,
                  });
                  setShowDeleteWorkspaceModal(account);
                }}
              />
            </div>
          )}
          {accounts?.length === 0 && (
            <div className="flex h-auto items-center justify-center pb-60 sm:h-full">
              <NoAccounts />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function Header({ onNewWorkspaceClick }: { onNewWorkspaceClick: () => void }) {
  const { firebaseUser } = useContext(AuthContext);
  const { appName, logoFull } = useWhitelabel();
  const [open, setOpen] = useState(false);

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen}>
      <header>
        <div className="mx-auto max-w-[1440px] px-3 py-2 sm:px-4 lg:divide-y lg:divide-gray-200 lg:px-6 lg:py-4">
          <div className="relative flex justify-between">
            <div className="relative z-10 flex items-center">
              <img className="w-[134px]" src={logoFull} alt={appName} />
            </div>
            <div className="relative z-10 flex items-center lg:hidden">
              {/* Mobile menu button */}
              <CollapsibleTrigger className="focus:outline-hidden relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                <span className="absolute -inset-0.5" />
                <span className="sr-only">Open menu</span>
                {open ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </CollapsibleTrigger>
            </div>

            <div className="hidden lg:relative lg:z-10 lg:ml-4 lg:flex lg:items-center">
              <HeaderButtons onNewWorkspaceClick={onNewWorkspaceClick} />
            </div>
          </div>
        </div>

        <CollapsibleContent asChild>
          <nav className="lg:hidden" aria-label="Global">
            <div className="pb-3 pt-4">
              <div className="flex items-center px-4">
                <div className="shrink-0">
                  {firebaseUser?.photoURL ? (
                    <img
                      className="h-10 w-10 rounded-full"
                      src={firebaseUser?.photoURL}
                      alt=""
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-100" />
                  )}
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {firebaseUser?.displayName ?? ''}
                  </div>
                  <div className="text-sm font-medium text-gray-500">
                    {firebaseUser?.email ?? ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="focus:outline-hidden relative ml-auto shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className={'mt-4 px-4'}>
                <HeaderButtons onNewWorkspaceClick={onNewWorkspaceClick} />
              </div>
            </div>
          </nav>
        </CollapsibleContent>
      </header>
    </Collapsible>
  );
}

const HeaderButtons = ({
  onNewWorkspaceClick,
}: {
  onNewWorkspaceClick: () => void;
}) => {
  const { logout } = useContext(AuthContext);
  const { track } = useAnalytics();

  return (
    <div className={'flex justify-between gap-2.5'}>
      <Button
        type={'button'}
        className={
          'rounded-2lg gap-2.5 px-4 py-[9px] tracking-[0.02em] has-[>svg]:px-4'
        }
        onClick={onNewWorkspaceClick}
      >
        <Plus className={'size-5'} /> New Workspace
      </Button>
      <Button
        type={'button'}
        variant={'outline'}
        className={'rounded-2lg px-[15px] py-2'}
        onClick={() => {
          track('logout_clicked', {
            from: 'workspaces_screen',
          });
          logout();
        }}
      >
        Logout
      </Button>
    </div>
  );
};
